import React, { useMemo, useState } from 'react';
import { useWallet, WalletAdapterName } from '@/contexts/WalletContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import IconSolflare from '@/assets/icons/solflare.jpeg';
import IconPhantom from '@/assets/icons/phantom.svg';
import IconBackpack from '@/assets/icons/backpack.png';
import IconMagicEden from '@/assets/icons/magiceden.png';
import IconMWA from '@/assets/icons/solana.svg'; // MWA uses Solana logo (universal wallet adapter)
import { useConnectWallet, usePrimaryWallet, useWalletBalance, useWalletList } from '@/hooks/useWalletQueries';
import { isMobileDevice, isWalletAvailable } from '@/lib/mobileWalletUtils';
import { trackEvent } from '@/utils/analytics';
import { EventAction } from '@/types/analytics';
import { isMWASupported, isMWAAvailable } from '@/lib/mwaAdapter';
import { useToast } from '@/hooks/use-toast';
import { parseNormalizedError } from '@/lib/queryClient';
import useWalletModalStore from '@/stores/useWalletModalStore';


const WalletModal: React.FC = () => {
  const { data: primaryWalletData } = usePrimaryWallet();
  const primaryWallet = primaryWalletData?.wallet;
  const { publicKey, wallet, connect, disconnect, select } = useWallet();
  const { visible, setVisible } = useWalletModalStore();
  const { toast } = useToast();
  const isMobile = isMobileDevice();
  const walletListQuery = useWalletList();
  const { mutateAsync: connectWalletAsync } = useConnectWallet();
  const [newConnectedWallet, setNewConnectedWallet] = useState<string>();
  const walletList = walletListQuery.data?.wallets || [];
  const isWalletExists = useMemo(() => walletList.some(wallet => wallet?.publicKey.toString() === newConnectedWallet), [walletList.length, newConnectedWallet]);
  const { refetch: refetchCurrentBalance } = useWalletBalance(newConnectedWallet, isWalletExists);

  // Base wallets available on both desktop and mobile
  const baseWallets: { name: WalletAdapterName; icon: string }[] = [
    { name: 'Phantom', icon: IconPhantom },
    { name: 'Solflare', icon: IconSolflare },
    { name: 'Backpack', icon: IconBackpack },
    { name: 'MagicEden', icon: IconMagicEden },
  ];

  // MWA is mobile-only AND requires Android Chrome
  // Only show MWA option if platform is supported
  const wallets = isMobile && isMWASupported()
    ? [...baseWallets, { name: 'MWA' as WalletAdapterName, icon: IconMWA }]
    : baseWallets;

  const getWalletDescription = (walletName: WalletAdapterName): string | null => {
    if (!isMobile) return null; // No description for desktop

    const available = isWalletAvailable(walletName);

    if (available) {
      return 'Connected';
    }

    switch (walletName) {
      case 'Phantom':
        return 'Tap to open Phantom app';
      case 'Solflare':
      case 'Backpack':
      case 'MagicEden':
        return 'Opens wallet in-app browser';
      case 'MWA':
        // Check if MWA wallets are available
        if (isMWAAvailable()) {
          return 'Connect Solana wallet';
        } else {
          return 'No wallets detected';
        }
      default:
        return null;
    }
  };

  const handleWalletSelect = async (walletName: WalletAdapterName) => {
    const deviceType = isMobile ? 'mobile' : 'desktop';

    // Track wallet selection
    trackEvent('wallet', EventAction.BUTTON_CLICK, {
      label: `select_wallet_${walletName.toLowerCase()}`,
      component: 'wallet_modal',
      wallet_name: walletName,
      device_type: deviceType
    });

    // Check MWA availability before attempting connection
    if (walletName === 'MWA' && !isMWAAvailable()) {
      toast({
        title: 'No Wallets Found',
        description: 'Please install a Solana mobile wallet like Phantom, Solflare, or Ultimate to continue.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const publicKey = await connect(walletName);
      
      if (!publicKey) {
        return;
      }

      if (walletList.some(wallet => wallet.publicKey.toString() === publicKey.toString())) {
          toast({
              title: 'Connection Failed',
              description: 'Wallet is already connected',
              variant: 'destructive',
          });
          return;
      }

      try {
          // Get the current wallet adapter name
          const walletAdapter = walletName;
          setNewConnectedWallet(publicKey.toString());
          await connectWalletAsync({
              publicKey: publicKey.toString(),
              walletAdapter: walletAdapter,
          });

          // Fetch on-chain SOL balance from server
          try {
              await refetchCurrentBalance();
          } catch (balanceError: any) {
              console.warn('Failed to fetch wallet balance:', balanceError?.message || balanceError);
          }

          toast({
              title: 'Wallet Connected',
              description: `Successfully connected ${publicKey.toString().slice(0, 4)}...${publicKey.toString().slice(-4)}`,
          });

          // Disconnect after registration - wallet is now saved to backend and can be reconnected later
          // This allows users to register multiple wallets without keeping them all connected
          await disconnect();

          // If a primary wallet is connected, reconnect it
          if (primaryWallet?.walletType === 'connected' && primaryWallet.walletAdapter) {
              select(primaryWallet.walletAdapter as WalletAdapterName);
              // Small delay to ensure adapter is ready after disconnect
              await new Promise(resolve => setTimeout(resolve, 100));
              await connect(primaryWallet.walletAdapter as WalletAdapterName);
          }
      } catch (error: any) {
          console.error('Error connecting wallet:', error);
          // If wallet is already connected (409), do nothing
          if (error.status === 409) {
              return;
          }
          const parsed = parseNormalizedError(error);
          toast({
              title: 'Connection Failed',
              description: parsed.message || 'Failed to connect wallet. Please try again.',
              variant: 'destructive',
          });
      }
      
      // Track successful connection
      trackEvent('wallet', EventAction.WALLET_CONNECT, {
        label: 'wallet_connect_success',
        component: 'wallet_modal',
        wallet_name: walletName,
        device_type: deviceType
      });

      setVisible(false);
    } catch (error: any) {
      console.error('Error connecting to wallet:', error);

      // Track failed connection
      trackEvent('wallet', EventAction.WALLET_CONNECT, {
        label: 'wallet_connect_failed',
        component: 'wallet_modal',
        wallet_name: walletName,
        device_type: deviceType,
        error_message: error.message || 'Unknown error'
      });

      // Don't show error for redirecting errors (user is being redirected)
      if (!error.message?.includes('Redirecting')) {
        toast({
          title: 'Connection Failed',
          description: error.message || 'Failed to connect wallet',
          variant: 'destructive',
        });
      }
    }
  };

  const handleModalClose = (open: boolean) => {
    if (!open) {
      // Track modal close (via close button or ESC key)
      trackEvent('ui_interaction', EventAction.MODAL_CLOSE, {
        label: 'wallet_modal_close',
        component: 'wallet_modal',
        device_type: isMobile ? 'mobile' : 'desktop'
      });
    }
    setVisible(open);
  };

  const handleBackdropClose = () => {
    // Track backdrop close
    trackEvent('ui_interaction', EventAction.MODAL_CLOSE, {
      label: 'wallet_modal_backdrop',
      component: 'wallet_modal',
      device_type: isMobile ? 'mobile' : 'desktop'
    });
  };

  return (
    <Dialog open={visible} onOpenChange={handleModalClose}>
      <DialogContent
        className="sm:max-w-[425px] bg-backgroundGray border-[#2A2A2A]"
        onInteractOutside={handleBackdropClose}
      >
        <DialogHeader>
          <DialogTitle className="text-white text-xl font-inter">Connect Wallet</DialogTitle>
          <DialogDescription className="text-tertiary font-inter">
            {isMobile
              ? 'Select a wallet to connect. You may be redirected to the wallet app.'
              : 'Select a wallet to connect to the application'
            }
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 mt-4">
          {wallets.map((wallet) => {
            const mobileDescription = getWalletDescription(wallet.name);

            return (
              <button
                key={wallet.name}
                onClick={() => handleWalletSelect(wallet.name)}
                className="flex items-center gap-3 px-4 py-3 rounded-lg bg-black hover:bg-[#1A1A1A] transition-colors border border-[#2A2A2A]"
              >
                <img src={wallet.icon} alt={wallet.name} className="w-6 h-6 rounded-lg" />
                <div className="flex-1 flex flex-col items-start">
                  <span className="text-white font-inter font-medium">{wallet.name}</span>
                  {mobileDescription && (
                    <span className="text-xs text-[#888] font-inter">{mobileDescription}</span>
                  )}
                </div>
                {(wallet.name === primaryWallet?.walletAdapter && publicKey?.toBase58() === primaryWallet?.publicKey) && (
                  <span className="text-sm text-[#888] font-inter">Primary</span>
                )}
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WalletModal;
